import { PublicClientApplication } from "@azure/msal-browser";

import type { AuthClient } from "./auth-client";

/**
 * The page Entra returns every auth response to — see auth/redirect.html and
 * src/auth/redirect-bridge.ts. It must be registered verbatim as an SPA
 * redirect URI on the app registration, for every origin the dashboard runs on.
 */
const REDIRECT_PATH = "/auth/redirect.html";

/**
 * Marks that we already bounced this browsing session through an interactive
 * redirect to repair a failed silent renewal. Without it a token that keeps
 * failing after the redirect would send the user round Entra forever.
 */
const RECOVERY_KEY = "moreta.admin.silent-recovery";

const readRecoveryMark = (): boolean => {
  try {
    return window.sessionStorage.getItem(RECOVERY_KEY) !== null;
  } catch {
    // Storage disabled — fall back to "not yet tried"; the worst case is one
    // extra redirect, which is still better than a dead-end error screen.
    return false;
  }
};

const writeRecoveryMark = (mark: boolean): void => {
  try {
    if (mark) window.sessionStorage.setItem(RECOVERY_KEY, "1");
    else window.sessionStorage.removeItem(RECOVERY_KEY);
  } catch {
    // Ignore — see readRecoveryMark.
  }
};

const requireEnv = (name: string, value: string | undefined): string => {
  if (!value) {
    throw new Error(
      `Missing ${name}. Set it in the environment (see .env.example), or use VITE_AUTH_MODE=local for local development.`,
    );
  }
  return value;
};

export const createMsalAuthClient = (): AuthClient => {
  let pca: PublicClientApplication | null = null;
  let scopes: string[] = [];
  // Set as soon as a redirect is initiated. The browser is navigating away,
  // so every caller — including the other chart queries running in parallel —
  // must stop rather than retry unauthenticated or start a second
  // interaction (MSAL answers that with interaction_in_progress).
  let redirecting = false;

  // Every caller currently parked in navigatingAway(). They are released only
  // if the navigation turns out never to happen.
  const parked = new Set<(reason: unknown) => void>();

  /** Halts the caller for the lifetime of the page unload. */
  const navigatingAway = <T>(): Promise<T> =>
    new Promise<T>((_resolve, reject) => {
      parked.add(reject);
    });

  /**
   * Start a redirect. These normally never resolve — the browser navigates
   * away — but MSAL does reject them when the navigation never happens (its
   * redirect timeout, a blocked navigation). Leaving `redirecting` set in that
   * case wedges the app: every later token acquisition and every Sign in click
   * would hand back a promise that never settles, until a full page reload.
   * The callers already parked have to be released too, or the queries that
   * were in flight when the redirect started hang for the life of the page.
   */
  const startRedirect = async (
    redirect: () => Promise<void>,
  ): Promise<void> => {
    redirecting = true;
    try {
      await redirect();
    } catch (error) {
      redirecting = false;
      for (const reject of parked) reject(error);
      parked.clear();
      throw error;
    }
  };

  const instance = (): PublicClientApplication => {
    if (!pca) throw new Error("MSAL used before initialize()");
    return pca;
  };

  const activeAccount = () =>
    pca ? (pca.getActiveAccount() ?? pca.getAllAccounts()[0] ?? null) : null;

  return {
    mode: "msal",

    async initialize() {
      // Env is read here rather than at construction so a misconfigured
      // deploy surfaces in AuthProvider's "Sign-in unavailable" card — a
      // module-scope throw would white-screen the app before it mounts.
      const clientId = requireEnv(
        "VITE_ENTRA_CLIENT_ID",
        import.meta.env.VITE_ENTRA_CLIENT_ID,
      );
      const tenantId = requireEnv(
        "VITE_ENTRA_TENANT_ID",
        import.meta.env.VITE_ENTRA_TENANT_ID,
      );
      // Must be the exposed API scope whose access tokens carry
      // aud = ADMIN_OAUTH_AUDIENCE — the same scope Retool requests today.
      const scope = requireEnv(
        "VITE_ENTRA_SCOPE",
        import.meta.env.VITE_ENTRA_SCOPE,
      );

      pca = new PublicClientApplication({
        auth: {
          clientId,
          authority: `https://login.microsoftonline.com/${tenantId}`,
          // Not the app itself: msal-browser v5 hands the silent-renewal
          // iframe's response back over a BroadcastChannel that only the
          // redirect bridge posts to, so the response has to land on a page
          // that runs it. The bridge forwards interactive sign-ins on to the
          // route the user started from.
          redirectUri: `${window.location.origin}${REDIRECT_PATH}`,
          postLogoutRedirectUri: window.location.origin,
        },
        // localStorage keeps the session across tabs for an internal tool.
        cache: { cacheLocation: "localStorage" },
      });
      scopes = [scope];

      await pca.initialize();
      const result = await pca.handleRedirectPromise();
      if (result?.account) {
        pca.setActiveAccount(result.account);
      } else {
        const existing = activeAccount();
        if (existing) pca.setActiveAccount(existing);
      }
    },

    isAuthenticated() {
      return activeAccount() !== null;
    },

    getAccount() {
      const account = activeAccount();
      return account ? { email: account.username, name: account.name } : null;
    },

    async getAccessToken(options) {
      if (redirecting) return navigatingAway<string>();
      const account = activeAccount();
      if (!account) {
        await startRedirect(() => instance().loginRedirect({ scopes }));
        return navigatingAway<string>(); // page navigates away
      }
      try {
        const result = await instance().acquireTokenSilent({
          scopes,
          account,
          forceRefresh: options?.forceRefresh ?? false,
        });
        writeRecoveryMark(false);
        return result.accessToken;
      } catch (error) {
        // A parallel caller may have started the redirect while this request
        // was still in flight — the guard at the top of the function ran
        // before that. Park rather than report a failure on a page that is
        // already leaving, and rather than consume the one-shot marker twice.
        if (redirecting) return navigatingAway<string>();
        // Any silent failure is recoverable by sending the user through Entra
        // interactively — and it has to be, because the useful ones are not
        // all InteractionRequiredAuthError. A broken silent-iframe round trip
        // rejects with BrowserAuthError("timed_out"), which under a narrower
        // check surfaces to the user as a dead-end error with a Retry button
        // that re-enters the same failure.
        if (readRecoveryMark()) throw error;
        // Has to be written before the redirect starts: once the navigation
        // happens nothing here runs again.
        writeRecoveryMark(true);
        try {
          await startRedirect(() =>
            instance().acquireTokenRedirect({ scopes, account }),
          );
        } catch (redirectError) {
          // The navigation never happened (MSAL's redirect timeout, a blocked
          // navigation), so this session never actually spent its one trip
          // through Entra. Leaving the marker set would make every later
          // silent failure rethrow at the check above — Retry could not
          // recover the tab short of a new browsing session.
          writeRecoveryMark(false);
          throw redirectError;
        }
        return navigatingAway<string>(); // page navigates away
      }
    },

    async login() {
      if (redirecting) return navigatingAway<void>();
      await startRedirect(() => instance().loginRedirect({ scopes }));
    },

    async logout() {
      await instance().logoutRedirect();
    },
  };
};
